// =============================================================================
// Input sanitization utilities for user-controlled form fields
//
// These fields are interpolated into LLM prompts, so we apply:
//   1. Whitespace normalization (safeText)
//   2. Character allowlists (no special chars that could break prompt structure)
//   3. Length limits per field type
//
// Prompt armoring (XML delimiters) is handled separately in the prompt builders.
// =============================================================================

/**
 * Collapse all whitespace (including newlines) to single spaces and trim.
 * This prevents prompt injection via multi-line input that could break
 * out of markdown bullet points or inject new sections.
 */
export function safeText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.replace(/\s+/g, ' ').trim();
}

// Character allowlist: letters (any script/accent), digits, spaces,
// hyphens, apostrophes, periods, commas. This covers legitimate names,
// professions, and city/country names across languages.
const ALLOWED_CHARS_PATTERN = /^[\p{L}\p{N}\s\-''.,()/&]+$/u;

export interface SanitizeOptions {
  /** Maximum character length */
  maxLength: number;
  /** Whether digits are allowed (e.g., for professions like "3D Artist") */
  allowDigits?: boolean;
}

/** Field-specific constraints */
export const FIELD_LIMITS = {
  displayName: { maxLength: 50 },
  profession: { maxLength: 80, allowDigits: true },
  destinationCity: { maxLength: 60 },
  destinationCountry: { maxLength: 60 },
} as const;

export interface SanitizeResult {
  /** The sanitized value (empty string if rejected) */
  value: string;
  /** Whether the input passed validation */
  valid: boolean;
  /** Reason for rejection, if any */
  reason?: string;
}

/**
 * Sanitize a user-provided free-text field.
 *
 * Returns the cleaned value if valid, or an empty string with a reason
 * if rejected. This is used both for frontend validation feedback and
 * server-side enforcement in the edge function.
 */
export function sanitizeUserInput(raw: unknown, opts: SanitizeOptions): SanitizeResult {
  const text = safeText(raw);

  if (text.length === 0) {
    return { value: '', valid: true };
  }

  if (text.length > opts.maxLength) {
    return {
      value: '',
      valid: false,
      reason: `Must be ${opts.maxLength} characters or fewer`,
    };
  }

  if (!ALLOWED_CHARS_PATTERN.test(text)) {
    return {
      value: '',
      valid: false,
      reason: 'Contains characters that are not allowed',
    };
  }

  return { value: text, valid: true };
}

/**
 * Quick boolean check — useful in Zod .refine() validators.
 */
export function isValidUserInput(raw: unknown, opts: SanitizeOptions): boolean {
  return sanitizeUserInput(raw, opts).valid;
}

// =============================================================================
// AI output sanitization
// =============================================================================

/**
 * Strip `<user_input>...</user_input>` tags from AI-generated text,
 * keeping the inner content. These tags are used as prompt armoring in
 * AI prompts but should never appear in the final output.
 */
export function stripPromptTags(text: string): string {
  return text.replace(/<\/?user_input>/g, '');
}

/**
 * Recursively strip prompt tags from all string values in an object.
 * Used to clean AI-generated JSON (blocks, welcome page, etc.) before
 * storing or rendering.
 */
export function deepStripPromptTags<T>(value: T): T {
  if (typeof value === 'string') {
    return stripPromptTags(value) as T;
  }
  if (Array.isArray(value)) {
    return value.map(deepStripPromptTags) as T;
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = deepStripPromptTags(v);
    }
    return result as T;
  }
  return value;
}
